import React from 'react';
import { Card, Space, Button } from 'antd';
import { CarOutlined } from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import SfExpressCard from './SfExpressCard';
import JdExpressCard from './JdExpressCard';
import { SfExpressOrder } from '../../types/sf-express.types';
import { JdExpressOrder } from '../../types/jd-express.types';

// Helper function to get localized SF Express button text
const getSfExpressButtonText = (locale: string): string => {
    switch (locale) {
        case 'zh-CN':
            return '创建顺丰订单';
        case 'zh-HK':
            return '創建順豐訂單';
        default:
            return 'Create SF Order';
    }
};

// Helper function to get localized JD Express button text
const getJdExpressButtonText = (locale: string): string => {
    switch (locale) {
        case 'zh-CN':
            return '创建京东订单';
        case 'zh-HK':
            return '創建京東訂單';
        default:
            return 'Create JD Order';
    }
};

interface DeliveryProviderCardProps {
    appointmentId?: string;
    onCreateSfExpressOrder: () => Promise<void>;
    creatingSfOrder: boolean;
    onCreateJdExpressOrder?: () => Promise<void>;
    creatingJdOrder?: boolean;
    onCloudPrintWaybill?: (waybillNo: string) => Promise<void>;
    onViewSfExpressPdf?: (waybillNo: string) => void;
    onViewJdExpressPdf?: (waybillNo: string) => void;
    onDeleteSfExpressOrder?: () => void;
    onDeleteJdExpressOrder?: () => void;
    showSfExpressButton?: boolean;
    showJdExpressButton?: boolean;
    showRouteLookup?: boolean;
    style?: React.CSSProperties;
    locale?: string;
    sfExpressOrders?: SfExpressOrder[];
    jdExpressOrders?: JdExpressOrder[];
    title?: string;
    showTitle?: boolean;
}

const DeliveryProviderCard: React.FC<DeliveryProviderCardProps> = ({
    appointmentId,
    onCreateSfExpressOrder,
    creatingSfOrder,
    onCreateJdExpressOrder,
    creatingJdOrder = false,
    onCloudPrintWaybill,
    onViewSfExpressPdf,
    onViewJdExpressPdf,
    onDeleteSfExpressOrder,
    onDeleteJdExpressOrder,
    showSfExpressButton = true,
    showJdExpressButton = true,
    showRouteLookup = true,
    style,
    locale,
    sfExpressOrders = [],
    jdExpressOrders = [],
    title,
    showTitle = true
}) => {
    const { t, currentLanguage } = useTranslation();

    // Enhanced locale detection and validation
    const isValidLocale = (loc: string): boolean => {
        return ['en', 'zh-CN', 'zh-HK'].includes(loc);
    };
    
    const validatedLocale = isValidLocale(locale || currentLanguage) ? (locale || currentLanguage) : 'en';

    // Check if there are any orders with waybill numbers
    const hasSfWaybill = sfExpressOrders.some(order => order.waybill_no);
    const hasJdWaybill = jdExpressOrders.some(order => order.waybill_no);
    const hasAnyWaybill = hasSfWaybill || hasJdWaybill;

    // Don't render if no delivery provider functionality is needed
    if (!showSfExpressButton && !showJdExpressButton && !hasAnyWaybill) {
        return null;
    }

    const handleCreateSfExpressOrder = async () => {
        try {
            await onCreateSfExpressOrder();
        } catch (error) {
            console.error('Error creating SF Express order:', error);
        }
    };

    const handleCreateJdExpressOrder = async () => {
        try {
            if (onCreateJdExpressOrder) {
                await onCreateJdExpressOrder();
            }
        } catch (error) {
            console.error('Error creating JD Express order:', error);
        }
    };

    return (
        <Card
            title={showTitle ? (title || t('deliveryProvider.title') || 'Delivery Provider') : undefined}
            style={{ 
                marginBottom: 24, 
                ...style 
            }}
            extra={
                <Space>
                    {/* SF Express Create Button */}
                    {showSfExpressButton && !hasAnyWaybill && (
                        <Button
                            type="primary"
                            icon={<CarOutlined />}
                            size="small"
                            onClick={handleCreateSfExpressOrder}
                            loading={creatingSfOrder}
                            title={getSfExpressButtonText(validatedLocale)}
                        >
                            {getSfExpressButtonText(validatedLocale)}
                        </Button>
                    )}
                    
                    {/* JD Express Create Button */}
                    {showJdExpressButton && !hasAnyWaybill && (
                        <Button
                            type="primary"
                            icon={<CarOutlined />}
                            size="small"
                            onClick={handleCreateJdExpressOrder}
                            loading={creatingJdOrder}
                            title={getJdExpressButtonText(validatedLocale)}
                        >
                            {getJdExpressButtonText(validatedLocale)}
                        </Button>
                    )}
                </Space>
            }
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* SF Express Card Component */}
                {showSfExpressButton && !hasJdWaybill && (
                    <SfExpressCard
                        appointmentId={appointmentId}
                        onCreateSfExpressOrder={() => Promise.resolve()}
                        creatingSfOrder={false}
                        onViewSfExpressPdf={onViewSfExpressPdf}
                        onDeleteSfExpressOrder={onDeleteSfExpressOrder}
                        showSfExpressButton={false}
                        showRouteLookup={showRouteLookup}
                        locale={locale}
                        sfExpressOrders={sfExpressOrders}
                        showTitle={false}
                    />
                )}

                {/* JD Express Card Component */}
                {showJdExpressButton && !hasSfWaybill && (
                    <JdExpressCard
                        appointmentId={appointmentId}
                        onCreateJdExpressOrder={() => Promise.resolve()}
                        creatingJdOrder={false}
                        onCloudPrintWaybill={onCloudPrintWaybill}
                        onViewJdExpressPdf={onViewJdExpressPdf}
                        onDeleteJdExpressOrder={onDeleteJdExpressOrder}
                        showJdExpressButton={false}
                        showRouteLookup={showRouteLookup}
                        locale={locale}
                        jdExpressOrders={jdExpressOrders}
                        showTitle={false}
                    />
                )}
            </Space>
        </Card>
    );
};

export default DeliveryProviderCard;
